/**
 * Barra di navigazione laterale con gruppi collassabili e modalita rail.
 *
 * 220px espansa, 52px rail. Sfondo bianco / #1A1E2A dark.
 * Bordo destro 0.5px #D1D5DB / #2E3447 dark.
 *
 * Gruppi collassabili con icona + label maiuscola + freccia.
 * In modalita rail: solo icone centrate, gruppi non collassabili.
 * Footer: bottone Esci con hover rosso.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from 'lucide-react';
import { registeredModules } from '../registry/modules';
import { sidebarGroups } from '../registry/groups';
import { useUO } from './UOContext';
import { useAuth, type MenuMode } from './AuthContext';

interface SidebarProps {
  /** Se true la sidebar e in modalita rail (solo icone). */
  collapsed: boolean;
  /** Callback per toggle collapsed. */
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { enabledModules, currentUO } = useUO();

  /** Verifica se un modulo frontend è visibile nel ramo corrente. */
  const isModuleVisible = (moduloNome: string): boolean => {
    const mod = registeredModules.find((m) => m.nome === moduloNome);
    if (!mod) return false;
    // Moduli senza backendModulo sono sempre visibili (es. Sistema)
    if (!mod.backendModulo) return true;
    // Se nessun ramo selezionato, mostra tutto
    if (!currentUO) return true;
    // Filtra per moduli abilitati nel ramo
    return enabledModules.includes(mod.backendModulo);
  };

  /** Gruppo hovered per effetto hover sulla label. */
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);

  /** Route hovered per effetto hover sulle voci. */
  const [hoveredRoute, setHoveredRoute] = useState<string | null>(null);

  /** Verifica se una route e attiva. */
  const isRouteActive = (path: string): boolean => {
    if (path === location.pathname) return true;
    if (path.endsWith('/') && location.pathname.startsWith(path)) return true;
    if (!path.includes(':') && location.pathname.startsWith(path + '/')) return true;
    return false;
  };

  const { user } = useAuth();
  const menuMode: MenuMode = user?.menu_mode ?? 'libera';
  const storageKey = user ? `floraltech.sidebar.openGroups.${user.id}` : null;

  /** Gruppo che contiene la route attiva (per auto-apertura). */
  const gruppoAttivo = useMemo(() => {
    for (const g of sidebarGroups) {
      for (const nome of g.moduli) {
        const mod = registeredModules.find((m) => m.nome === nome);
        if (mod && mod.routes.some((r) => isRouteActive(r.path))) return g.label;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const tuttiAperti = () => {
    const init: Record<string, boolean> = {};
    for (const g of sidebarGroups) init[g.label] = true;
    return init;
  };

  const soloAttivo = () => {
    const init: Record<string, boolean> = {};
    for (const g of sidebarGroups) init[g.label] = g.label === gruppoAttivo;
    return init;
  };

  const leggiStorage = (): Record<string, boolean> | null => {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  };

  /** Stato apertura per gruppo, ricalcolato al cambio di modalita'/utente. */
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(tuttiAperti);

  useEffect(() => {
    if (menuMode === 'tutto_aperto') setOpenGroups(tuttiAperti());
    else if (menuMode === 'libera') setOpenGroups(leggiStorage() ?? tuttiAperti());
    else setOpenGroups(soloAttivo()); // tutto_chiuso e fisarmonica partono chiusi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuMode, user?.id]);

  /** Auto-apertura del gruppo attivo nelle modalita' con gruppi chiusi. */
  useEffect(() => {
    if (!gruppoAttivo) return;
    if (menuMode === 'tutto_chiuso') {
      setOpenGroups((prev) => ({ ...prev, [gruppoAttivo]: true }));
    } else if (menuMode === 'fisarmonica') {
      setOpenGroups(soloAttivo());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gruppoAttivo, menuMode]);

  const toggleGroup = (label: string) => {
    if (collapsed || menuMode === 'tutto_aperto') return;
    setOpenGroups((prev) => {
      let next: Record<string, boolean>;
      if (menuMode === 'fisarmonica') {
        next = {};
        for (const g of sidebarGroups) next[g.label] = g.label === label && !prev[label];
      } else {
        next = { ...prev, [label]: !prev[label] };
      }
      if (menuMode === 'libera' && storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* storage pieno o negato: la preferenza di sessione resta in memoria */
        }
      }
      return next;
    });
  };

  return (
    <aside
      className="flex h-screen flex-col transition-all duration-300 ease-erp"
      style={{
        width: collapsed ? 52 : 220,
        minWidth: collapsed ? 52 : 220,
        backgroundColor: 'var(--color-sidebar)',
        borderRight: '0.5px solid var(--color-sidebar-border)',
      }}
    >
      {/* Toggle rail — posizionato in alto a destra */}
      <div
        className="flex items-center border-b"
        style={{
          height: 48,
          justifyContent: collapsed ? 'center' : 'flex-end',
          paddingRight: collapsed ? 0 : 10,
          borderColor: 'var(--color-sidebar-border)',
        }}
      >
        <button
          onClick={onToggle}
          className="flex items-center justify-center rounded-md transition-colors duration-150"
          style={{
            width: 28,
            height: 28,
            color: 'var(--color-sidebar-group-label)',
          }}
          title={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
        >
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>

      {/* Navigazione gruppi */}
      <nav className="flex-1 overflow-y-auto py-2">
        {sidebarGroups.map((group) => {
          const GroupIcon = group.icon;
          const isOpen = menuMode === 'tutto_aperto' ? true : (openGroups[group.label] ?? false);
          const isGroupHovered = hoveredGroup === group.label;

          /* Una voce per modulo: link alla prima route visibile, attivo su qualsiasi route del modulo */
          const moduleEntries: { nome: string; path: string; icon?: LucideIcon; routes: { path: string }[] }[] = [];
          for (const nome of group.moduli) {
            if (!isModuleVisible(nome)) continue;
            const mod = registeredModules.find((m) => m.nome === nome);
            if (!mod) continue;
            const firstVisible = mod.routes.find((r) => !r.hidden && r.label);
            if (!firstVisible) continue;
            moduleEntries.push({
              nome: mod.nome,
              path: firstVisible.path,
              icon: firstVisible.icon,
              routes: mod.routes,
            });
          }
          if (moduleEntries.length === 0) return null;

          return (
            <div key={group.label} className="mb-1">
              {/* Header gruppo */}
              {(() => {
                const isInterattivo = menuMode !== 'tutto_aperto';
                const HeaderTag = isInterattivo ? 'button' : 'div';
                return (
                  <HeaderTag
                    onClick={isInterattivo ? () => toggleGroup(group.label) : undefined}
                    onMouseEnter={isInterattivo ? () => setHoveredGroup(group.label) : undefined}
                    onMouseLeave={isInterattivo ? () => setHoveredGroup(null) : undefined}
                    className="flex w-full items-center gap-2 px-3 py-2 transition-colors duration-150"
                    style={{
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      color: isGroupHovered
                        ? 'var(--color-sidebar-group-label-hover)'
                        : 'var(--color-sidebar-group-label)',
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      cursor: isInterattivo ? 'pointer' : 'default',
                    }}
                    title={collapsed ? group.label : undefined}
                  >
                    {GroupIcon && <GroupIcon size={16} className="flex-shrink-0" />}
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">{group.label}</span>
                        {isInterattivo &&
                          (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                      </>
                    )}
                  </HeaderTag>
                );
              })()}

              {/* Voci del gruppo */}
              {(collapsed || isOpen) && (
                <div>
                  {moduleEntries.map((entry) => {
                    const Icon = entry.icon;
                    const active = entry.routes.some((r) => isRouteActive(r.path));
                    const isHovered = hoveredRoute === entry.nome && !active;

                    return (
                      <NavLink
                        key={entry.nome}
                        to={entry.path}
                        onMouseEnter={() => setHoveredRoute(entry.nome)}
                        onMouseLeave={() => setHoveredRoute(null)}
                        className="mx-1 flex items-center gap-2 rounded-md transition-colors duration-150 ease-erp"
                        style={{
                          justifyContent: collapsed ? 'center' : 'flex-start',
                          paddingLeft: collapsed ? 0 : 30,
                          paddingRight: collapsed ? 0 : 8,
                          paddingTop: 6,
                          paddingBottom: 6,
                          fontSize: 13,
                          fontWeight: active ? 500 : 400,
                          backgroundColor: active
                            ? 'var(--color-sidebar-active)'
                            : isHovered
                              ? 'var(--color-sidebar-item-hover-bg)'
                              : 'transparent',
                          color: active
                            ? 'var(--color-sidebar-active-foreground)'
                            : isHovered
                              ? 'var(--color-sidebar-item-hover-text)'
                              : 'var(--color-sidebar-item)',
                        }}
                        title={collapsed ? entry.nome : undefined}
                      >
                        {Icon && <Icon size={16} className="flex-shrink-0" />}
                        {!collapsed && <span>{entry.nome}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

    </aside>
  );
};

export default Sidebar;
